import { useCallback, useEffect, useRef, useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import {
  RETRY_POLL_MILLISECONDS, finishedRetries, finishedRetryNotice, interpretRetryError, interpretRetryResponse,
  isRetrying, pauseNotice, retryingLabel, shouldPoll,
} from "@/lib/emailFailures";

const TONE_CLASS = {
  ok: "bg-green-50 border-green-600 text-green-800",
  error: "bg-red-50 border-red-600 text-red-800",
  info: "bg-blue-50 border-blue-600 text-blue-800",
};

const KIND_LABEL = { temporary: "Fallo temporal agotado", permanent: "Fallo permanente" };

function formatDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// Correos con fallo definitivo, solo para admin y tech_admin (el backend también lo exige).
// El reintento es de un solo uso: el botón se bloquea al primer clic. Mientras un reintento sigue
// en curso la fila muestra quién lo pidió, sin botón, y el panel consulta de nuevo cada pocos
// segundos hasta que termina (enviado o falló definitivamente). Si la cola de envío está en pausa
// por la configuración del correo, un aviso lo dice (RF-4, RF-18). Las reglas viven en el backend.
export default function EmailFailuresPanel() {
  const [items, setItems] = useState([]);
  const [queuePaused, setQueuePaused] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null); // { tone, text }
  const [locked, setLocked] = useState({}); // id -> true while the click is being answered
  const inFlight = useRef(new Set());
  const itemsRef = useRef([]);

  // When a row that was running a retry is gone, ask how it ended and say so.
  const announceFinished = useCallback(async (ids) => {
    for (const id of ids) {
      let state = null;
      try {
        const { data } = await http.get("/emails", { params: { message_id: id, type: "state_changed", limit: 1 } });
        state = data.items?.[0]?.data?.state || null;
      } catch (e) {
        state = null;
      }
      const result = finishedRetryNotice(state);
      setNotice({ tone: result.tone, text: result.text });
    }
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError("");
      setNotice(null); // a refresh replaces the previous retry message
    }
    try {
      const { data } = await http.get("/emails/failures", { params: { limit: 100 } });
      const next = data.items || [];
      const finished = finishedRetries(itemsRef.current, next);
      itemsRef.current = next;
      setItems(next);
      setQueuePaused(data.queue_paused || null);
      if (finished.length) announceFinished(finished);
    } catch (e) {
      if (!silent) setError(formatApiErrorDetail(e.response?.data?.detail) || "No se pudieron cargar los envíos con fallo.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [announceFinished]);

  useEffect(() => { load(); }, [load]);

  const polling = shouldPoll(items);
  useEffect(() => {
    if (!polling) return undefined;
    const timer = setInterval(() => load({ silent: true }), RETRY_POLL_MILLISECONDS);
    return () => clearInterval(timer);
  }, [polling, load]);

  const retry = async (id) => {
    if (inFlight.current.has(id)) return; // second click while the first is running
    inFlight.current.add(id);
    setLocked((prev) => ({ ...prev, [id]: true }));
    let outcome;
    try {
      const response = await http.post(`/emails/${encodeURIComponent(id)}/retry`);
      outcome = interpretRetryResponse(response.status, response.data);
    } catch (e) {
      outcome = interpretRetryError(e);
    } finally {
      inFlight.current.delete(id);
    }
    setNotice({ tone: outcome.tone, text: outcome.text });
    if (outcome.remove) {
      itemsRef.current = itemsRef.current.filter((item) => item.id !== id);
      setItems(itemsRef.current);
    }
    setLocked((prev) => ({ ...prev, [id]: outcome.keepBusy }));
    if (outcome.reload) load({ silent: true }); // shows the "reintento en curso" label
  };

  const pause = pauseNotice(queuePaused);
  if (loading) return null;
  if (!error && items.length === 0 && !notice && !pause) return null;

  return (
    <section className="mb-8" data-testid="email-failures-panel" aria-label="Correos con fallo definitivo">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-extrabold text-[var(--ink)]">
          <i className="fa-solid fa-envelope-circle-check text-[var(--lala-red,#E4002B)] mr-2"></i>
          Correos que no se pudieron enviar
        </h2>
        <button onClick={() => load()} className="text-sm text-[var(--ink-soft)] hover:text-[var(--lala-blue)]"
          data-testid="email-failures-refresh">
          Actualizar
        </button>
      </div>

      {pause && (
        <div role="alert" data-testid="email-queue-paused"
          className="mb-3 p-3 rounded-xl border-2 border-amber-500 bg-amber-50 text-amber-900 text-sm">
          <strong>{pause.title}.</strong> {pause.text}
        </div>
      )}
      {notice && (
        <div role="status" data-testid="email-failures-notice"
          className={`mb-3 p-3 rounded-xl border-2 text-sm flex justify-between gap-4 ${TONE_CLASS[notice.tone]}`}>
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} aria-label="Cerrar aviso" className="font-bold">×</button>
        </div>
      )}
      {error && <div className="mb-3 p-3 rounded-xl border-2 border-red-600 bg-red-50 text-red-800 text-sm">{error}</div>}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} data-testid={`email-failure-${item.id}`}
            className="p-4 rounded-2xl border-2 border-[var(--lala-red,#E4002B)]/30 bg-white flex gap-4 items-start">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-bold text-[var(--ink)] truncate">{item.subject}</h3>
                <span className="text-xs text-[var(--ink-soft)]">{formatDate(item.updated_at)}</span>
              </div>
              <p className="text-sm text-[var(--ink-soft)]">
                Para <strong>{item.original_recipient}</strong> · {item.use_case} · {item.attempts_used} intento(s)
                {item.attachments?.length > 0 && ` · ${item.attachments.length} adjunto(s)`}
              </p>
              <p className="text-sm text-red-700 mt-1" data-testid="email-failure-reason">
                {KIND_LABEL[item.failure_kind] || "Fallo"}: {item.failure_reason || "sin detalle"}
              </p>
            </div>
            {isRetrying(item) ? (
              <span data-testid={`email-retrying-${item.id}`} role="status"
                className="px-3 py-2 rounded-xl bg-blue-50 border-2 border-blue-600 text-blue-800 text-sm font-semibold max-w-[16rem]">
                <i className="fa-solid fa-spinner fa-spin mr-2"></i>{retryingLabel(item)}
              </span>
            ) : (
              <button
                onClick={() => retry(item.id)}
                disabled={!!locked[item.id]}
                data-testid={`email-retry-${item.id}`}
                className="px-4 py-2 rounded-full bg-[var(--lala-blue)] text-white font-semibold text-sm whitespace-nowrap hover:bg-[var(--lala-blue-deep)] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {locked[item.id] ? "Reintentando…" : "Reintentar"}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
